// Reports what a signed-in user did in an upsell (shown, a question opened,
// Go Pro, a way out, closed) for the experiments admin's per-arm funnel. The
// server stamps the experiment arm from its own stored assignment; the client
// only says what happened, in which showing.

import apiClient from 'src/helpers/apiClient';
import {
  FunnelEventBodySurfaceEnum,
  type FunnelEventBodyEventTypeEnum,
} from 'src/openapi';

export type FunnelEventType = FunnelEventBodyEventTypeEnum;

export interface FunnelEvent {
  /** One showing of the upsell (newInstanceId); a checkout started from it
   * passes the same id, which is how the order is tied back to the arm. */
  instanceId: string;
  eventType: FunnelEventType;
  placement?: string;
  lead?: string;
  /** The arm whose copy is on screen, if any. The server stamps the event
   * only when it matches the arm it stored, so it can't name an arm. */
  variant?: string;
  detail?: string;
}

/** An id for one showing of an upsell. */
export const newInstanceId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** Reports an upsell event on `surface`: the pitch, or the small Pro card. */
export const logUpsellEvent = (
  surface: FunnelEventBodySurfaceEnum,
  { instanceId, eventType, placement, lead, variant, detail }: FunnelEvent,
) => {
  // Signed-in users only: a guest's 401 would send the auth interceptor
  // through a refresh and a logout for a call nobody is waiting on.
  if (!apiClient.apiKey) return;
  apiClient
    .getExperimentApi()
    .then((api) =>
      api.experimentApiLogFunnelEvent({
        funnelEventBody: {
          surface,
          instance_id: instanceId,
          event_type: eventType,
          placement,
          lead,
          variant,
          detail,
        },
      }),
    )
    .catch(() => {
      // best-effort: a lost event must never surface to the buyer
    });
};

export const logProPitchEvent = (event: FunnelEvent) =>
  logUpsellEvent(FunnelEventBodySurfaceEnum.ProPitch, event);

export const logProCardEvent = (event: FunnelEvent) =>
  logUpsellEvent(FunnelEventBodySurfaceEnum.ProCard, event);
